// src/lib/coachApi.js
// STATUS: active | UPDATED: 2026-08-27
// LOG:
//   2026-08-27  Send the Supabase access token with every coach call. The
//               function now requires a signed-in session, so a request without
//               this header comes back 401.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
//   2026-08-01  New: client for the coach Netlify function + local transcript store
// Client for the coach Netlify function.
//
// The transcript lives on the server now, in chat_sessions and chat_messages.
// It used to be localStorage only, which meant clearing site data erased
// somebody's coaching history and a salesperson who used a phone and a desk had
// two unrelated coaches.
//
// localStorage is kept as a cache, not the record. It paints the conversation
// instantly on load while the server copy is fetched, because a coach that
// shows a blank screen for 300ms every time reads as broken.

import { supabase } from './supabase'

const ENDPOINT = '/.netlify/functions/coach'

function transcriptKey(userId) {
  return `j13-coach-${userId || 'anon'}`
}

function sessionKey(userId) {
  return `j13-coach-session-${userId || 'anon'}`
}

export function loadSessionId(userId) {
  try {
    return localStorage.getItem(sessionKey(userId)) || null
  } catch {
    return null
  }
}

function saveSessionId(userId, id) {
  if (!id) return
  try {
    localStorage.setItem(sessionKey(userId), id)
  } catch {
    /* private mode: the session is still on the server, just not resumed here */
  }
}

// The authoritative transcript. Reads under RLS, which scopes it to this user
// without any role check here (chat_sessions_self_all, chat_messages_via_session).
export async function loadRemoteTranscript(userId) {
  if (!userId) return { messages: [], sessionId: null, lastAt: null }
  try {
    const { data: sessions, error: sErr } = await supabase
      .from('chat_sessions')
      .select('id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
    if (sErr || !sessions || sessions.length === 0) return { messages: [], sessionId: null, lastAt: null }

    const sessionId = sessions[0].id
    const { data: msgs, error: mErr } = await supabase
      .from('chat_messages')
      .select('role, content, created_at')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .limit(120)
    if (mErr) return { messages: [], sessionId, lastAt: null }

    const kept = (msgs || []).filter((m) => m.role === 'user' || m.role === 'assistant')

    return {
      messages: kept.map((m) => ({ role: m.role, content: m.content })),
      sessionId,
      // When the conversation last moved. The caller uses it to tell a refresh
      // from a return, and created_at was already being fetched and thrown away.
      lastAt: kept.length > 0 ? kept[kept.length - 1].created_at || null : null,
    }
  } catch {
    return { messages: [], sessionId: null, lastAt: null }
  }
}

export function loadTranscript(userId) {
  try {
    const raw = localStorage.getItem(transcriptKey(userId))
    if (raw) return JSON.parse(raw)
  } catch {
    /* fall through to empty transcript */
  }
  return []
}

export function saveTranscript(userId, messages) {
  try {
    localStorage.setItem(transcriptKey(userId), JSON.stringify(messages.slice(-60)))
  } catch {
    /* storage unavailable: transcript is session-only */
  }
}

export function clearTranscript(userId) {
  try {
    localStorage.removeItem(transcriptKey(userId))
  } catch {
    /* nothing to clear */
  }
}

// READ A SERVER SENT EVENT STREAM, AND BE HONEST ABOUT WHAT CAN GO WRONG.
//
// The wire format is one JSON object per `data:` line, with a `type`. Two
// kinds: `delta` carries a fragment of the reply as the model writes it, and
// `done` carries the authoritative payload the server persisted.
//
// THE CLIENT DOES NOT TRUST WHAT IT CAUGHT. It shows the deltas live, then
// replaces the whole thing with `done.reply`. A fragment lost to a flaky
// connection therefore repairs itself the moment the reply finishes, and the
// transcript always matches what the server actually wrote to the database
// rather than what happened to arrive.
//
// BUFFERING IS THE BUG THAT IS EASY TO MISS. A chunk from the network is not
// a line: one read can carry half an event, or three and a half. Anything
// after the last blank line stays in the buffer for the next read. Getting
// this wrong produces a stream that works perfectly on a fast local connection
// and drops words on a phone.
async function readStream(res, onDelta) {
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let done = null

  for (;;) {
    const { value, done: finished } = await reader.read()
    if (finished) break
    buffer += decoder.decode(value, { stream: true })

    // Events are separated by a blank line. Keep the tail, which is either
    // empty or a partial event still arriving.
    const parts = buffer.split('\n\n')
    buffer = parts.pop() || ''

    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data:'))
      if (!line) continue
      let payload
      try {
        payload = JSON.parse(line.slice(5).trim())
      } catch {
        // A truncated event is not worth taking the reply down for. The
        // `done` payload at the end repairs whatever this would have shown.
        continue
      }
      if (payload.type === 'delta' && payload.text) onDelta(payload.text)
      else if (payload.type === 'done') done = payload
    }
  }

  return done
}

// THE OFF SWITCH, AND WHY IT IS A CONSTANT RATHER THAN AN ENV VAR.
//
// Streaming shipped on 2026-09-26 verified as far as it can be without a
// signed in session: the v2 function is served, both statuses are right, and
// the reader survives every awkward chunk boundary there is. What is NOT
// verified is a live model round trip, because exercising one means signing in
// as a real person.
//
// The failure mode is degraded rather than catastrophic. A throw anywhere in
// the stream is caught server side and becomes an ordinary reply, and a stream
// that ends early becomes "that got cut off" rather than a hang. But the coach
// is the most important thing in this product, so turning it off should take
// ten seconds and no thinking.
//
// Set this to false and the coach goes back to complete replies, which is the
// same function with the streaming turned off rather than a different code
// path. An env var would have been the obvious choice and is worse: a VITE_
// variable is compiled into the bundle, so changing it costs a rebuild and a
// deploy, which is exactly what you do not want when something is wrong.
//
// Delete this once a real session has streamed a real reply.
const STREAMING = true

// The two replies that are about the connection rather than about coaching.
// Named so the streaming and non-streaming paths return the same words, and
// so a future third path cannot invent a third wording.
const expired = () => ({
  reply: 'Your session has expired. Sign in again and we will pick up where we left off.',
  configured: true,
  unauthenticated: true,
})

// `onDelta` is optional. Passing it asks the server to stream and shows the
// reply as it is written; leaving it out is byte for byte the old behaviour,
// which is why the fallback cannot rot into a second implementation.
export async function sendToCoach(messages, context, userId, mode = null, onDelta = null) {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    // No live session. Say so plainly rather than firing a request we know the
    // function will reject.
    return {
      reply: 'Your session has expired. Sign in again and we will pick up where we left off.',
      configured: true,
      unauthenticated: true,
    }
  }

  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
      context,
      // 'method' switches the coach into interviewing Jazz about his own
      // scripts, and the server re-checks the caller is Level 00 before
      // honouring it. 'intro' is the coach meeting somebody for the first time,
      // which anybody may do.
      mode,
      sessionId: loadSessionId(userId),
      // Opt in. A request without this gets the complete-reply path, which is
      // the same function with the streaming turned off rather than a second
      // code path that can drift.
      stream: STREAMING && Boolean(onDelta),
    }),
  })

  // A STREAMING RESPONSE IS ALWAYS 200, AND THAT BROKE THIS CHECK.
  //
  // The status line goes out with the first byte, long before the function
  // knows whether the session is any good, so a rejected request arrives as
  // 200 with the real status inside the final event. Found on 2026-09-26 by
  // running an unauthenticated streaming request against the dev server: the
  // reply came back undefined instead of "your session has expired", because
  // this branch was reading an HTTP status that can no longer carry the answer.
  //
  // `statusOf` is the single place that decides. Both paths go through it, so
  // they cannot drift.
  if (res.status === 401) return expired()

  // A 404 here means the functions are not running rather than anything being
  // wrong with the coach. Vite alone does not serve netlify/functions, so
  // /.netlify/functions/coach is not a route and the coach looks disconnected
  // on localhost no matter what the API key is set to. `yarn dev` is netlify
  // dev now, which serves the functions next to the app; `yarn dev:vite` is
  // the front-end-only escape hatch and the coach cannot work under it.
  if (res.status === 404) {
    // THE DEVELOPER DETAIL GOES TO THE CONSOLE, NOT TO THE PERSON.
    //
    // This string used to be shown in the chat, and it named Vite, Netlify
    // functions and two yarn commands. A salesperson between customers, or
    // Jazz, would have been handed somebody else's debugging notes by a coach
    // that is supposed to sound like a person who has done the job. Nobody
    // using this product should ever learn what it is built on.
    console.error(
      'coach: 404 from /.netlify/functions/coach. On localhost use `yarn dev` (netlify dev) rather than `yarn dev:vite`, since Vite alone does not serve the functions. On the live site this means the function failed to deploy.'
    )
    return {
      reply: 'I cannot reach the coach right now. Give it a minute and try me again.',
      configured: false,
      functionMissing: true,
    }
  }

  if (!res.ok) {
    throw new Error(`coach request failed: ${res.status}`)
  }

  // A streamed response, but only if the server actually sent one. An older
  // deploy, or a proxy that rewrote the response, falls through to JSON rather
  // than hanging on a reader that will never produce an event.
  const streamed =
    STREAMING &&
    onDelta &&
    res.body &&
    (res.headers.get('content-type') || '').includes('text/event-stream')

  let data
  if (streamed) {
    const done = await readStream(res, onDelta)

    // No `done` event means the stream ended early, which is a real failure
    // rather than an empty reply. Say so in the coach's own voice rather than
    // leaving whatever fragments arrived sitting there looking finished.
    if (!done) {
      return {
        reply: 'That got cut off before I finished. Ask me again and I will pick it up.',
        configured: true,
        interrupted: true,
      }
    }

    // The status the FUNCTION decided, which the HTTP status could not carry.
    if (done.status === 401) return expired()
    if (done.status && done.status >= 400 && !done.reply) {
      // Any other refusal that came with no reply to show. The error text is
      // ours and goes to the console, never into the transcript: nobody using
      // this product should be handed somebody else's error string.
      console.error('coach: stream ended with status', done.status, done.error || '')
      return {
        reply: 'I could not pick that up. Try me again in a moment.',
        configured: true,
      }
    }
    data = done
  } else {
    data = await res.json()
  }
  // The function tells us which session it wrote into, including when it had to
  // start a new one because the id we sent was not ours.
  if (data.sessionId) saveSessionId(userId, data.sessionId)
  return data
}
